import { ValidationError } from "sequelize"
import {
    ASC, DESC,
    NOW, NEXT_2_DAYS,
    YESTERDAY, CURRENT_DAY, TOMORROW, THIS_WEEK, THIS_MONTH,
    LAST_7_DAYS, LAST_30_DAYS, LAST_60_DAYS, LAST_90_DAYS,
    EXACT_DATE, CUSTOM_RANGE,
} from "const"
import { OrderType, DeadlineFilters } from "client/utils"
import { LangType } from "client/store"

export const compare = (A: any, B: any, order: OrderType) => {
    let res = 0
    if (order === ASC) {
        if (A > B) res = 1
        else if (A < B) res = -1
    } else if (order === DESC) {
        if (A < B) res = 1
        else if (A > B) res = -1
    }
    return res
}

export const sort = (a: any, b: any, column: string, order: OrderType) => {
    let A = a[column]
    let B = b[column]
    if (A && B && typeof A === "string") {
        A = A.toUpperCase()
        B = B.toUpperCase()
    }

    return compare(A, B, order)
}

export const sortByRevision = (a: string, b: string, sortOrder: OrderType) => {
    const dot = "."

    const arrayA = a ? a.split(dot) : []
    const arrayB = b ? b.split(dot) : []
    const length = Math.min(arrayA.length, arrayB.length)

    let A: string
    let B: string
    let intA: number
    let intB: number
    let res = 0

    for (let i = 0; i < length; i++) {
        A = arrayA[i]
        B = arrayB[i]
        intA = parseInt(A, 10)
        intB = parseInt(B, 10)

        if (Number.isNaN(intA) && Number.isNaN(intB)) {
            if (sortOrder === ASC) {
                if (A > B) res = 1
                else res = -1
            } else if (A < B) res = 1
            else res = -1
            break
        }

        if (Number.isNaN(intA) && !Number.isNaN(intB)) {
            if (sortOrder === ASC) res = 1
            else res = -1
            break
        }

        if (!Number.isNaN(intA) && Number.isNaN(intB)) {
            if (sortOrder === ASC) res = -1
            else res = 1
            break
        }

        if (intA !== intB) {
            if (sortOrder === ASC) {
                if (intA > intB) res = 1
                else res = -1
            } else if (intA < intB) res = 1
            else res = -1
            break
        }
    }

    if (res !== 0) return res

    if (sortOrder === ASC) {
        if (arrayA.length > arrayB.length) return 1
        return -1
    }

    if (arrayA.length < arrayB.length) return 1
    return -1
}

export const filterByDeadline = (filters: DeadlineFilters, deadline: Date) => {
    const now = new Date()

    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)

    const tomorrowStart = new Date(todayStart)
    tomorrowStart.setDate(tomorrowStart.getDate() + 1)

    const tomorrowEnd = new Date(tomorrowStart)
    tomorrowEnd.setDate(tomorrowEnd.getDate() + 1)

    const yesterdayStart = new Date(todayStart)
    yesterdayStart.setDate(yesterdayStart.getDate() - 1)

    const next2days = new Date(tomorrowStart) // without today
    next2days.setDate(next2days.getDate() + 2)

    const next7days = new Date(todayStart) // this week
    next7days.setDate(next7days.getDate() + 7)

    const next30days = new Date(todayStart) // this month
    next30days.setMonth(next30days.getMonth() + 1)

    const last7days = new Date(todayStart)
    last7days.setDate(last7days.getDate() - 7)

    const last30days = new Date(todayStart)
    last30days.setMonth(last30days.getMonth() - 1)

    const last60days = new Date(todayStart)
    last60days.setMonth(last60days.getMonth() - 2)

    const last90days = new Date(todayStart)
    last90days.setMonth(last90days.getMonth() - 3)


    let exactDateStart: Date = null
    let exactDateEnd: Date = null
    if (filters.deadlineExactDate) {
        exactDateStart = new Date(filters.deadlineExactDate)
        exactDateStart.setHours(0, 0, 0, 0)

        exactDateEnd = new Date(exactDateStart)
        exactDateEnd.setDate(exactDateEnd.getDate() + 1)
    }

    let customRangeStart: Date = null
    let customRangeEnd: Date = null
    if (filters.deadlineCustomRangeStart) {
        customRangeStart = new Date(filters.deadlineCustomRangeStart)
        customRangeStart.setHours(0, 0, 0, 0)
    }
    if (filters.deadlineCustomRangeEnd) {
        customRangeEnd = new Date(filters.deadlineCustomRangeEnd)
        customRangeEnd.setHours(0, 0, 0, 0)
        customRangeEnd.setDate(customRangeEnd.getDate() + 1) // include the end date
    }

    let showRow = true

    if (filters.overdue) {
        if (!deadline) showRow = false
        else if (filters.overdue === NOW && deadline > now) showRow = false
        else if (
            filters.overdue === NEXT_2_DAYS
            && (deadline < now || deadline > next2days)
        ) showRow = false
    } else if (filters.deadline) {
        if (!deadline) showRow = false
        else if (
            filters.deadline === YESTERDAY
            && (deadline < yesterdayStart || deadline > todayStart)
        ) showRow = false
        else if (
            filters.deadline === CURRENT_DAY
            && (deadline < todayStart || deadline > tomorrowStart)
        ) showRow = false
        else if (
            filters.deadline === TOMORROW
            && (deadline < tomorrowStart || deadline > tomorrowEnd)
        ) showRow = false
        else if (
            filters.deadline === THIS_WEEK
            && (deadline < todayStart || deadline > next7days)
        ) showRow = false
        else if (
            filters.deadline === THIS_MONTH
            && (deadline < todayStart || deadline > next30days)
        ) showRow = false
        else if (
            filters.deadline === LAST_7_DAYS
            && (deadline < last7days || deadline > todayStart)
        ) showRow = false
        else if (
            filters.deadline === LAST_30_DAYS
            && (deadline < last30days || deadline > todayStart)
        ) showRow = false
        else if (
            filters.deadline === LAST_60_DAYS
            && (deadline < last60days || deadline > todayStart)
        ) showRow = false
        else if (
            filters.deadline === LAST_90_DAYS
            && (deadline < last90days || deadline > todayStart)
        ) showRow = false
        else if (
            filters.deadline === EXACT_DATE
            && exactDateStart
            && exactDateEnd
            && (deadline < exactDateStart || deadline > exactDateEnd)
        ) showRow = false
        else if (filters.deadline === CUSTOM_RANGE) {
            if (customRangeStart && deadline < customRangeStart) showRow = false
            if (customRangeEnd && deadline > customRangeEnd) showRow = false
        }
    }

    return showRow
}

export const search = (pattern: string, target: any) => {
    let hasMatch = false
    Object.keys(target).forEach((key) => {
        const property = target[key]
        if (property && property.toString().toUpperCase().indexOf(pattern.toUpperCase()) >= 0) hasMatch = true
    })
    return hasMatch
}

export const validate = (error: ValidationError, lang: LangType) => {
    if (!error || !error.errors || !error.errors.length) return null

    const validation: any = {}
    let pathArray: string[]
    let path: string

    error.errors.forEach((err: any) => {
        switch (err.validatorKey) {
            case "notEmpty":
            case "is_null":
                validation[err.path] = lang.validationFieldRequired
                break
            case "len":
                validation[err.path] = lang.validationMaxLength.replace("{0}", err.validatorArgs[1])
                break
            case "not_unique":
                pathArray = err.path.split(".")
                path = pathArray[pathArray.length - 1].replace(/_([a-z])/g, (g) => g[1].toUpperCase()) // convert to camelCase
                if (path === "PRIMARY") path = "id"
                validation[path] = lang.validationNotUnique
                break
            default:
                validation[err.path] = lang.validationUnknown
                break
        }
    })
    return validation
}
